import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageMeta, Reveal, SectionHead } from "@/components/shared";

const CASES = [
  {
    title: "Northvolt Ett",
    meta: "EV Battery Manufacturing — Sweden",
    body: "Professional HSE consultancy experience within a major EV battery manufacturing environment, supporting risk management, contractor coordination, site assurance and proactive safety culture.",
    attribution: "Professional assignment through The PAC Group",
    logos: ["NORTHVOLT", "THE PAC GROUP"],
  },
  {
    title: "Google Data Centre",
    meta: "Mission Critical — UK",
    body: "HSE delivery within a critical, mission-critical environment — covering equipment unloading and ingress, installation activities, MEWP and work-at-height controls, and day-to-day HSE coordination.",
    attribution: "Selected professional experience; assignment undertaken through the employing contractor",
  },
  {
    title: "Müller UK & Ireland",
    meta: "Food & Beverage Manufacturing — UK",
    body: "Site HSE leadership including inspections, audits, KPI reporting, risk controls, training, stakeholder coordination and continual improvement across a major manufacturing operation.",
    attribution: "Selected professional experience",
  },
  {
    title: "Automation Equipment Installation",
    meta: "Automation & Robotics — Multi-site",
    body: "Contractor coordination and control of high-risk activities during automation and equipment installations — permits, risk assessments, method statements and site compliance.",
    attribution: "Selected professional experience",
  },
  {
    title: "Process Plant Decommissioning",
    meta: "Industrial & Process — Multi-location",
    body: "Multi-location HSE coordination covering permits, inspections, KPIs, contractor activities and multiple concurrent work fronts within live process environments.",
    attribution: "Selected professional experience",
  },
  {
    title: "PepsiCo / Manufacturing Projects",
    meta: "Food & Beverage Manufacturing — UK",
    body: "Experience across equipment installation, automation, platform demolition, steelwork, packaging lines and sprinkler systems within major manufacturing environments.",
    attribution: "Selected professional experience",
  },
];

const ADDITIONAL = [
  "Amazon",
  "Kellogg's",
  "M&S",
  "Dairy Crest",
  "Aryzta",
  "PM Group",
  "NHS",
  "Bovis Homes",
  "Crown (Bevcan)",
  "Imtech Meica",
  "TSL",
  "Virtua",
  "Lorein Engineering",
];

export default function Experience() {
  return (
    <>
      <PageMeta
        title="HSE Experience & Expertise | SafetySymbiosis"
        description="Selected project environments and professional experience behind SafetySymbiosis's practical HSE and SHEQ leadership."
      />

      <section className="border-b border-brand-line bg-white py-14 lg:py-20">
        <div className="wrap">
          <Reveal>
            <span className="eyebrow">Experience</span>
            <h1 className="mt-5 max-w-3xl font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
              Experience &amp; Expertise
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-brand-charcoal/85 sm:text-lg">
              SafetySymbiosis' capability is underpinned by extensive
              professional HSE experience across complex projects, high-risk
              environments and operational workplaces.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="wrap">
          <SectionHead
            eyebrow="Selected Professional Experience"
            title="Selected Project Environments"
            intro="A small selection of the environments our leadership has worked in. Assignments shown were undertaken through the relevant employers and consultancy engagements."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {CASES.map((c, i) => (
              <Reveal key={c.title} delay={(i % 2) * 0.07}>
                <div className="card flex h-full flex-col p-8 transition-all duration-200 hover:border-brand-green/40 hover:shadow-lg hover:shadow-brand-navy/5">
                  <div className="flex items-baseline gap-4">
                    <span className="font-heading text-sm font-extrabold tracking-widest text-brand-green">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-heading text-xl font-bold leading-snug">{c.title}</h3>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wider text-brand-charcoal/60">
                        {c.meta}
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-brand-charcoal/85">{c.body}</p>
                  {c.logos && (
                    <div className="mt-5 flex flex-wrap gap-2.5">
                      {c.logos.map((l) => (
                        <span
                          key={l}
                          className="rounded-md border border-brand-line bg-brand-mist px-3.5 py-2 font-heading text-[13px] font-extrabold tracking-wide text-brand-navy/70"
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mt-5 border-t border-brand-line pt-4 text-xs italic leading-relaxed text-brand-charcoal/60">
                    {c.attribution}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-mist py-16 lg:py-20">
        <div className="wrap">
          <SectionHead
            eyebrow="Additional Experience"
            title="Additional Professional Experience"
            intro="Further organisations and environments from our leadership's professional background."
          />
          <Reveal className="mt-8 flex flex-wrap gap-2.5" delay={0.1}>
            {ADDITIONAL.map((a) => (
              <span
                key={a}
                className="rounded-full border border-brand-line bg-white px-4 py-2 text-[13px] font-semibold text-brand-navy/80"
                data-testid={`additional-org-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {a}
              </span>
            ))}
          </Reveal>
          <p className="mt-6 max-w-4xl text-xs leading-relaxed text-brand-charcoal/60">
            Organisations shown reflect selected professional and project
            environments gained through client, principal-contractor and
            specialist-contractor roles. They are not presented as direct
            clients of SafetySymbiosis.
          </p>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20">
        <div className="wrap flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">
              Bringing that experience to your project
            </h2>
            <p className="mt-2 max-w-xl text-sm text-brand-charcoal/80 sm:text-base">
              Talk to us about the environments, risks and standards your
              project works to.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/contact" className="btn-green" data-testid="experience-cta-contact">
              Discuss Your HSE Requirements
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
